/**
 * 将 Markdown 字符串转换为 .docx 并下载
 */

import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    AlignmentType,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    HeightRule,
    ImageRun,
    VerticalAlign,
} from 'docx';
import { marked } from 'marked';

// 假设所有图片均按 96 DPI 渲染（Word 默认）
const DPI = 96;
// 图片最大显示宽度，约15.90cm，适应 A4 内容区
const MAX_IMAGE_WIDTH = 15.90 / 2.54 * DPI;

/**
 * 异步解析内联 Token（支持图片）
 * 完美处理 **粗体**、*斜体*、`代码`、[链接]、![图片]
 */
async function createRunsFromTokensAsync(tokens, baseStyle = {}) {
    const runs = [];
    if (!Array.isArray(tokens)) return runs;

    for (const token of tokens) {
        try {
            switch (token.type) {
                case 'text': {
                    if (token.tokens && token.tokens.length > 0) {
                        const inner = await createRunsFromTokensAsync(token.tokens, baseStyle);
                        runs.push(...inner);
                    } else {
                        runs.push(new TextRun({
                            text: token.text,
                            font: baseStyle.font || 'SimSun',
                            size: baseStyle.size || 24,
                            color: baseStyle.color || '000000',
                            bold: baseStyle.bold || false,              //  加粗
                            italics: baseStyle.italics || false,        //  斜体
                            underline: baseStyle.underline || false,    //  下划线
                            strike: baseStyle.strike || false,          //  删除线
                        }));
                    }
                    break;
                }

                case 'strong': {
                    const inner = await createRunsFromTokensAsync(
                        token.tokens,
                        { ...baseStyle, bold: true }
                    );
                    runs.push(...inner);
                    break;
                }

                case 'em': {
                    const inner = await createRunsFromTokensAsync(
                        token.tokens,
                        { ...baseStyle, italics: true }
                    );
                    runs.push(...inner);
                    break;
                }

                case 'del': {
                    const inner = await createRunsFromTokensAsync(
                        token.tokens,
                        { ...baseStyle, strike: true }
                    );
                    runs.push(...inner);
                    break;
                }

                case 'codespan': {
                    runs.push(new TextRun({
                        text: token.text,
                        font: 'Courier New',
                        size: 22,
                        color: '000000',
                    }));
                    break;
                }

                case 'link': {
                    const inner = await createRunsFromTokensAsync(
                        token.tokens,
                        { ...baseStyle, color: '0563C1', underline: true }
                    );
                    runs.push(...inner);
                    break;
                }

                case 'image': {
                    const imgRun = await generateImageRun(token);
                    if (imgRun) {
                        runs.push(imgRun);
                    } else {
                        // 加载失败时显示占位文字
                        runs.push(new TextRun({
                            text: token.text || '[图片加载失败]',
                            font: 'SimSun',
                            size: 24,
                            italics: true,
                            color: 'FF0000',
                        }));
                    }
                    break;
                }

                case 'html': {
                    // 忽略内联 HTML，或取其文本
                    if (token.text) {
                        runs.push(new TextRun({
                            text: token.text,
                            font: baseStyle.font || 'SimSun',
                            size: baseStyle.size || 24,
                            color: baseStyle.color || '000000',
                        }));
                    }
                    break;
                }

                default: {
                    if (token.text) {
                        runs.push(new TextRun({
                            text: token.text,
                            font: baseStyle.font || 'SimSun',
                            size: baseStyle.size || 24,
                            color: baseStyle.color || '000000',
                        }));
                    } else if (token.tokens) {
                        const inner = await createRunsFromTokensAsync(
                            token.tokens,
                            baseStyle
                        );
                        runs.push(...inner);
                    }
                }
            }
        } catch (e) {
            console.warn('处理内联 token 出错:', token, e);
            runs.push(new TextRun({
                text: '[异常]',
                font: 'SimSun',
                size: 24,
                color: 'FF0000',
            }));
        }
    }
    return runs;
}

/**
 * 获取图片原始尺寸
 * @param {string} src - 图片源（URL 或 Base64）
 * @returns {Promise<{width: number, height: number}>}
 */
function getImageNaturalSize(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            reject(new Error('图片加载失败，无法获取尺寸'));
        };
        // 如果是跨域图片，需要设置 crossOrigin，但很多图床可能不支持，我们先用无跨域模式尝试
        img.src = src;
    });
}

/**
 * 根据图片 Token 生成 ImageRun
 * 自动等比例缩放，宽度不超过 MAX_IMAGE_WIDTH
 */
async function generateImageRun(imageToken) {
    const href = imageToken.href;
    if (!href) return null;

    let imgBuffer, type = 'png';

    try {
        // ---------- 1. 获取图片数据 ----------
        if (href.startsWith('data:image/')) {
            const match = href.match(/^data:image\/(\w+);base64,/);
            if (match) type = match[1];
            const base64Data = href.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            imgBuffer = new Uint8Array(byteNumbers).buffer;
        } else {
            const response = await fetch(href);
            if (!response.ok) throw new Error(`下载失败: ${response.status}`);
            const contentType = response.headers.get('Content-Type');
            if (contentType) {
                const mimeMatch = contentType.match(/image\/(\w+)/);
                if (mimeMatch) type = mimeMatch[1];
            }
            imgBuffer = await response.arrayBuffer();
        }

        // ---------- 2. 获取原始尺寸 ----------
        let naturalWidth, naturalHeight;
        try {
            const size = await getImageNaturalSize(href);
            naturalWidth = size.width;
            naturalHeight = size.height;
        } catch (e) {
            // 无法获取尺寸时（如跨域限制），回退到只设置宽度，让 Word 自适应
            console.warn('无法获取图片尺寸，使用仅宽度模式:', href);
            return new ImageRun({
                data: imgBuffer,
                transformation: { width: MAX_IMAGE_WIDTH },
                type,
            });
        }

        // ---------- 3. 等比计算高度 ----------
        let displayWidth, displayHeight;
        if (naturalWidth > MAX_IMAGE_WIDTH) {
            // 图片宽度大于最大宽度，缩小
            const scale = MAX_IMAGE_WIDTH / naturalWidth;
            displayWidth = MAX_IMAGE_WIDTH;
            displayHeight = Math.round(naturalHeight * scale);
        } else {
            // 图片宽度小于等于最大宽度，保持原始尺寸
            displayWidth = naturalWidth;
            displayHeight = naturalHeight;
        }

        return new ImageRun({
            data: imgBuffer,
            transformation: {
                width: displayWidth,
                height: displayHeight,
            },
            type,
        });

    } catch (err) {
        console.error('图片加载失败:', href, err);
        return null;
    }
}

/**
 * 主函数：将 Markdown 转换为 .docx 并下载
 */
export async function MarkdownToDocx(markdownText, options = {}) {
    const { fileName = 'document.docx' } = options;

    const tokens = marked.lexer(markdownText);
    const children = [];

    for (const token of tokens) {
        switch (token.type) {
            // ---- 标题（黑体，不加粗，但可保留 bold:true 以符合常规） ----
            case 'heading': {
                let size;
                if (token.depth === 1) size = 44;      // 22pt
                else if (token.depth === 2) size = 32; // 16pt
                else if (token.depth === 3) size = 28; // 14pt
                else size = 28;

                children.push(
                    new Paragraph({
                        heading: HeadingLevel[`HEADING_${token.depth}`],
                        alignment: AlignmentType.LEFT,
                        spacing: { before: 200, after: 100 },
                        children: [
                            new TextRun({
                                text: token.text,
                                font: 'SimSun',
                                size,
                                bold: true,
                                color: '000000',
                            }),
                        ],
                    })
                );
                break;
            }

            // ---- 普通段落（异步处理，支持图片） ----
            case 'paragraph': {
                if (token.tokens && token.tokens.length > 0) {
                    const runs = await createRunsFromTokensAsync(token.tokens);
                    children.push(
                        new Paragraph({
                            spacing: { after: 120 },
                            children: runs,
                        })
                    );
                } else if (token.text) {
                    children.push(
                        new Paragraph({
                            spacing: { after: 120 },
                            children: [new TextRun({ text: token.text, font: 'SimSun', size: 24, color: '000000' })],
                        })
                    );
                }
                break;
            }

            // ---- 列表（支持图片） ----
            case 'list': {
                for (const item of token.items) {
                    const runs = item.tokens
                        ? await createRunsFromTokensAsync(item.tokens)
                        : [new TextRun({ text: item.text, font: 'SimSun', size: 24, color: '000000' })];
                    children.push(
                        new Paragraph({
                            spacing: { after: 60 },
                            bullet: { level: 0 },
                            children: runs,
                        })
                    );
                }
                break;
            }

            // ---- 引用块 ----
            case 'blockquote': {
                const quoteText = token.tokens
                    .filter(t => t.type === 'paragraph')
                    .map(p => p.text)
                    .join('\n');
                children.push(
                    new Paragraph({
                        spacing: { after: 120 },
                        indent: { left: 720 },
                        children: [
                            new TextRun({ text: quoteText, font: 'SimSun', size: 24, italics: true, color: '333333' }),
                        ],
                    })
                );
                break;
            }

            // ---- 代码块 ----
            case 'code': {
                children.push(
                    new Paragraph({
                        spacing: { after: 120 },
                        shading: { fill: 'F2F2F2' },
                        children: [
                            new TextRun({ text: token.text, font: 'Courier New', size: 22, color: '000000' }),
                        ],
                    })
                );
                break;
            }

            // ---- 表格 ----
            case 'table': {
                const tableRows = [];
                const rowHeight = {
                    value: Math.round(0.8 * 567),   // 行高，0.8cm
                    rule: HeightRule.ATLEAST,
                };

                if (token.header) {
                    const headerCells = token.header.map(cell =>
                        new TableCell({
                            shading: { fill: 'D9D9D9' },            // 表头背景
                            verticalAlign: VerticalAlign.CENTER,    // 垂直居中
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: cell.text,
                                            font: 'SimSun',
                                            size: 24,
                                            bold: true,
                                            color: '000000',
                                        }),
                                    ],
                                }),
                            ],
                            borders: {
                                top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                right: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                            },
                        })
                    );
                    tableRows.push(new TableRow({ children: headerCells, height: rowHeight }));
                }

                if (token.rows) {
                    for (const row of token.rows) {
                        const dataCells = row.map(cell =>
                            new TableCell({
                                verticalAlign: VerticalAlign.CENTER,    // 垂直居中
                                children: [
                                    new Paragraph({
                                        children: [
                                            new TextRun({
                                                text: cell.text,
                                                font: 'SimSun',
                                                size: 24,
                                                color: '000000',
                                            }),
                                        ],
                                    }),
                                ],
                                borders: {
                                    top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                    bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                    left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                    right: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                },
                            })
                        );
                        tableRows.push(new TableRow({ children: dataCells, height: rowHeight }));
                    }
                }

                if (tableRows.length > 0) {
                    children.push(
                        new Table({
                            rows: tableRows,
                            width: { size: 100, type: WidthType.PERCENTAGE },
                        })
                    );
                }
                break;
            }

            // ---- 分隔线 ----
            case 'hr': {
                children.push(
                    new Paragraph({
                        spacing: { before: 200, after: 200 },
                        border: {
                            bottom: { style: BorderStyle.SINGLE, size: 6, color: '999999' },
                        },
                        children: [],
                    })
                );
                break;
            }

            // ---- 空行（保持段落间距） ----
            case 'space': {
                children.push(
                    new Paragraph({ spacing: { after: 60 }, children: [] })
                );
                break;
            }

            // 其他类型安全忽略
        }
    }

    const doc = new Document({
        styles: {
            default: {
                document: {
                    run: {
                        font: 'SimSun',
                        size: 24,
                    },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 },
                    },
                },
                children,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
}